import { edgeColour, contentBox } from './adColours';

// Stuart, 29 Oct: "logos need to be more prominent". Many logo files carry a
// wide empty margin (transparent, or the logo's own background colour), so
// the mark looks small in every box it's shown in. Shared by the staff ad
// preview (B-50) and the sponsor setup form, which trims at upload.

const ANALYSE_SIDE = 400;
export const TRIM_MAX_SIDE = 800;

export function loadImage(src, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('load'));
    img.src = src;
  });
}

// Crop to the box found on the small analysis canvas (w×h of the natural
// nw×nh), with a 3% breathing pad, at full resolution capped to
// TRIM_MAX_SIDE. A data URL, or null when there's little to gain (< 4%
// margin) or no ink was found.
export function trimLogoCanvas(img, data, w, h, edge, nw, nh, type = 'image/png') {
  const box = contentBox(data, w, h, { bgHex: edge.transparent ? null : edge.hex });
  if (!box || (box.w >= w * 0.96 && box.h >= h * 0.96)) return null;
  const pad = Math.round(Math.max(box.w, box.h) * 0.03);
  const fx = nw / w;
  const fy = nh / h;
  const sx = Math.max(0, (box.x - pad) * fx);
  const sy = Math.max(0, (box.y - pad) * fy);
  const sw = Math.min(nw - sx, (box.w + pad * 2) * fx);
  const sh = Math.min(nh - sy, (box.h + pad * 2) * fy);
  const out = Math.min(1, TRIM_MAX_SIDE / Math.max(sw, sh));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(sw * out));
  c.height = Math.max(1, Math.round(sh * out));
  const cx = c.getContext('2d');
  if (!cx) return null;
  if (type === 'image/jpeg') { cx.fillStyle = '#FFFFFF'; cx.fillRect(0, 0, c.width, c.height); }
  cx.drawImage(img, sx, sy, sw, sh, 0, 0, c.width, c.height);
  try { return c.toDataURL(type, 0.92); } catch { return null; }
}

// A logo the admin just picked (a data: URL). Resolves { src, trimmed }:
// the margin cropped and the size capped, or the file as it was when that
// changes nothing — also for SVGs (kept sharp) and anything we can't read.
export async function trimUploadedLogo(dataUrl) {
  const asIs = { src: dataUrl, trimmed: false };
  const mime = (/^data:([^;,]+)/i.exec(dataUrl || '') || [])[1] || '';
  if (!/^image\/(png|jpe?g|webp|gif)$/i.test(mime)) return asIs;
  // Keep a JPEG a JPEG (much smaller); everything else may be transparent.
  const type = /jpe?g/i.test(mime) ? 'image/jpeg' : 'image/png';
  try {
    const img = await loadImage(dataUrl, false);
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return asIs;
    const scale = Math.min(1, ANALYSE_SIDE / Math.max(nw, nh));
    const w = Math.max(1, Math.round(nw * scale));
    const h = Math.max(1, Math.round(nh * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return asIs;
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    const trimmed = trimLogoCanvas(img, data, w, h, edgeColour(data, w, h), nw, nh, type);
    if (trimmed) return { src: trimmed, trimmed: true };
    // Nothing to crop — still shrink a huge file to the cap.
    if (Math.max(nw, nh) <= TRIM_MAX_SIDE) return asIs;
    const out = TRIM_MAX_SIDE / Math.max(nw, nh);
    const c = document.createElement('canvas');
    c.width = Math.round(nw * out);
    c.height = Math.round(nh * out);
    const cx = c.getContext('2d');
    if (!cx) return asIs;
    if (type === 'image/jpeg') { cx.fillStyle = '#FFFFFF'; cx.fillRect(0, 0, c.width, c.height); }
    cx.drawImage(img, 0, 0, c.width, c.height);
    return { src: c.toDataURL(type, 0.92), trimmed: false };
  } catch {
    return asIs;
  }
}
